import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchCartState } from '@theme/cart-state';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * Authoritative item count.
   *
   * This used to be derived by parsing the visible glyph — but the glyph is
   * deliberately BLANK at 100+ (the bubble is a fixed-size dot), so
   * `parseInt('')` returned NaN for any cart of 100 or more. That NaN then
   * poisoned everything downstream: the additive branch in onCartUpdate
   * (`NaN + itemCount`), the freshness comparison in onPageShow, the value
   * written to sessionStorage, and the trigger's accessible name — which would
   * have announced "Cart NaN items" to a screen reader.
   *
   * Real state, seeded from data-cart-count (server-rendered, never truncated).
   * @type {number}
   */
  #count = 0;

  /** @type {number} */
  get currentCartCount() {
    return this.#count;
  }

  set currentCartCount(value) {
    const count = Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
    this.#count = count;

    // Visual glyph: intentionally blank past 99.
    this.refs.cartBubbleCount.textContent = count < 100 ? String(count) : '';

    // Machine-readable truth, so a re-connect after a morph re-seeds correctly.
    if (this.refs.cartBubble instanceof HTMLElement) {
      this.refs.cartBubble.dataset.cartCount = String(count);
    }

    this.#syncControlAccessibleName(count);
  }

  /**
   * WCAG 2.5.3 accessible name for the header cart control. Must contain the
   * visible "Cart" title AND the badge digit so speech-control users can select
   * the control by what they see: SortSite F96 concatenates the button's text
   * nodes ("Cart" + the glyph) and requires that string inside the name.
   *
   * "Cart 3 items" satisfies that and reads once. Keep in lockstep with
   * header-actions.liquid, which server-renders the same string.
   * @param {number} count
   * @returns {string}
   */
  #accessibleNameForCount(count) {
    const title = Theme.translations?.cart_title ?? 'Cart';
    const template =
      count === 1
        ? Theme.translations?.cart_item_count_one ?? '{{ count }} item'
        : Theme.translations?.cart_item_count_other ?? '{{ count }} items';

    return `${title} ${template.replace('{{ count }}', String(count))}`;
  }

  /**
   * The name lives on the wrapping button/link, outside this element, so a
   * count change has to be written there too. Hydration morphs the control
   * because it carries data-hydration-key="header-cart-trigger".
   * @param {number} count
   */
  #syncControlAccessibleName(count) {
    const control = this.closest('[data-testid="cart-drawer-trigger"], a.action__cart');

    if (!(control instanceof HTMLElement)) return;

    control.setAttribute('aria-label', this.#accessibleNameForCount(count));
  }

  /**
   * Re-seed after a hydration morph so #count and the parent aria-label match
   * the freshly morphed data-cart-count (connectedCallback does not re-run).
   */
  updatedCallback() {
    super.updatedCallback();

    const seeded = Number.parseInt(
      this.refs.cartBubble instanceof HTMLElement ? this.refs.cartBubble.dataset.cartCount ?? '' : '',
      10
    );

    this.currentCartCount = Number.isFinite(seeded) && seeded > 0 ? seeded : 0;
  }

  connectedCallback() {
    super.connectedCallback();

    const seeded = Number.parseInt(
      this.refs.cartBubble instanceof HTMLElement ? this.refs.cartBubble.dataset.cartCount ?? '' : '',
      10
    );
    // Seed #count only. Liquid already set the wrapping control's aria-label;
    // the setter would rewrite it on every page load for no benefit.
    this.#count = Number.isFinite(seeded) && seeded > 0 ? seeded : 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.syncCartCountFromServer();
    }
  };

  /**
   * Re-syncs the bubble with the server after a back/forward-cache restore.
   * The restored DOM (and sessionStorage, whose reconciliation window is
   * 10s) predates whatever happened at checkout — an order placed there
   * empties the cart, and only server truth reflects that. The cart items
   * sections re-render themselves the same way (component-cart-items.js).
   */
  syncCartCountFromServer = async () => {
    try {
      const cart = await fetchCartState({ fresh: true });

      if (typeof cart?.item_count === 'number' && cart.item_count !== this.currentCartCount) {
        this.renderCartBubble(cart.item_count, false, false);
      }
    } catch {
      // Network hiccup — keep the restored snapshot's count.
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // A FAILED add must never move the badge. product-form.js dispatches
    // CartAddEvent{didError:true} on every failure path — 422 quantity limits,
    // sold out, and network/parse failures — because sticky-add-to-cart.js only
    // clears its `data-puppet` flag on a cart event, and without one the main
    // Add to Cart button stays stuck for the rest of the session. But those
    // events carry `source: 'product-form-component'` and the attempted
    // quantity, and the branch below treats that source as ADDITIVE — so a
    // failed add incremented the badge over a cart the server never changed.
    // The shopper sees "3" with two items in the cart, and it persists until
    // the next absolute update.
    //
    // Guarding here rather than at the dispatch sites deliberately: all four
    // error dispatches in product-form.js share this shape (two of them predate
    // the network-failure paths), and any future dispatcher gets the same
    // protection for free.
    if (event.detail.data?.didError) return;

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
        })
      );
    } catch {
      // Storage-blocked contexts (Safari Private Browsing, embedded webviews)
      // throw here. This runs inside an async cart:update listener, so an
      // uncaught throw became an unhandled rejection AND skipped the bubble
      // animation below. The bubble is already painted from live cart data;
      // sessionStorage only speeds up the next page load.
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
